import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { Card } from 'react-bootstrap';

import {
  useGetAgencyTierWindowsQuery,
  useCreateAgencyTierWindowMutation,
  useUpdateAgencyTierWindowMutation,
  useDeleteAgencyTierWindowMutation,
} from '../../slices/agencyTierWindowApiSlice';

// One row = one HSE-style response-time band: "if the shift starts this
// many days out, the principal (Tier1) supplier has this many hours before
// this tier opens up". maxDaysOut left blank means open-ended (e.g. "8 days
// or more").
const emptyBand = () => ({ label: '', minDaysOut: '', maxDaysOut: '', hours: '' });

// A single tier's editable band table — Tier2, Tier3, Tier4, or any further
// tier an admin has added via "Add another tier" below.
const TierWindowCard = ({ tierWindow, onDeleted }) => {
  const [updateTier, { isLoading: isSaving }] = useUpdateAgencyTierWindowMutation();
  const [deleteTier, { isLoading: isDeleting }] = useDeleteAgencyTierWindowMutation();

  const [label, setLabel] = useState(tierWindow.label || tierWindow.tier);
  const [bands, setBands] = useState(
    (tierWindow.bands || []).map((b) => ({
      label: b.label || '',
      minDaysOut: b.minDaysOut ?? '',
      maxDaysOut: b.maxDaysOut === null || b.maxDaysOut === undefined ? '' : b.maxDaysOut,
      hours: b.hours ?? '',
    }))
  );

  const updateBand = (index, field, value) => {
    setBands((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
  };

  const addBand = () => setBands((prev) => [...prev, emptyBand()]);
  const removeBand = (index) => setBands((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!bands.length) {
      toast.error('At least one time band is required.');
      return;
    }
    for (const b of bands) {
      if (b.minDaysOut === '' || b.hours === '') {
        toast.error('Every band needs a "days out from" value and a number of hours.');
        return;
      }
    }
    try {
      await updateTier({
        tier: tierWindow.tier,
        label,
        bands: bands.map((b) => ({
          label: b.label,
          minDaysOut: Number(b.minDaysOut),
          maxDaysOut: b.maxDaysOut === '' ? null : Number(b.maxDaysOut),
          hours: Number(b.hours),
        })),
      }).unwrap();
      toast.success(`${tierWindow.tier} response times saved`);
    } catch (err) {
      toast.error(err?.data?.message || 'Could not save this tier\'s response times');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove the response-time config for ${tierWindow.tier}? Agencies in this tier will then stay hidden until it's re-created.`)) {
      return;
    }
    try {
      await deleteTier(tierWindow.tier).unwrap();
      toast.success(`Removed ${tierWindow.tier}`);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.data?.message || 'Could not remove this tier');
    }
  };

  return (
    <Card className="mb-4 custom-card-bg2">
      <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
        <span>{tierWindow.tier}</span>
        <IconButton size="small" color="error" onClick={handleDelete} disabled={isDeleting} title="Remove this tier's config">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Card.Header>
      <Card.Body>
        <TextField
          label="Display label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
          margin="dense"
          sx={{ maxWidth: 320, mb: 2 }}
        />

        <Box sx={{ overflowX: 'auto' }}>
          <table className="table table-sm table-bordered mb-2" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Band label</th>
                <th style={{ width: '18%' }}>Days out — from</th>
                <th style={{ width: '18%' }}>Days out — to (blank = no limit)</th>
                <th style={{ width: '18%' }}>Hours before this tier opens</th>
                <th style={{ width: '10%' }} />
              </tr>
            </thead>
            <tbody>
              {bands.map((band, i) => (
                <tr key={i}>
                  <td>
                    <TextField
                      size="small"
                      fullWidth
                      value={band.label}
                      onChange={(e) => updateBand(i, 'label', e.target.value)}
                      placeholder="e.g. Same day or next day"
                    />
                  </td>
                  <td>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                      value={band.minDaysOut}
                      onChange={(e) => updateBand(i, 'minDaysOut', e.target.value)}
                    />
                  </td>
                  <td>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                      value={band.maxDaysOut}
                      onChange={(e) => updateBand(i, 'maxDaysOut', e.target.value)}
                    />
                  </td>
                  <td>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                      value={band.hours}
                      onChange={(e) => updateBand(i, 'hours', e.target.value)}
                    />
                  </td>
                  <td className="text-center align-middle">
                    <IconButton size="small" color="error" onClick={() => removeBand(i)} title="Remove band">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        <Button startIcon={<AddIcon />} size="small" onClick={addBand} sx={{ mr: 2 }}>
          Add band
        </Button>
        <Button variant="contained" size="small" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </Card.Body>
    </Card>
  );
};

// Form for adding a tier beyond the standard Tier2/3/4 (e.g. "Tier5") —
// this only creates the response-time config; the tier itself still needs
// to be added to the Agency model's agencyTier options separately before
// any agency can actually be assigned to it.
const AddTierForm = ({ onCreated }) => {
  const [tier, setTier] = useState('');
  const [createTier, { isLoading }] = useCreateAgencyTierWindowMutation();

  const handleCreate = async () => {
    const tierName = tier.trim();
    if (!tierName) {
      toast.error('Enter a tier name, e.g. "Tier5".');
      return;
    }
    try {
      await createTier({ tier: tierName, label: tierName }).unwrap();
      toast.success(`${tierName} added — seeded with the default HSE bands, ready to edit above.`);
      setTier('');
      onCreated?.();
    } catch (err) {
      toast.error(err?.data?.message || 'Could not create this tier');
    }
  };

  return (
    <Card className="mb-4 custom-card-bg2">
      <Card.Header className="fw-bold">Add another tier</Card.Header>
      <Card.Body>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates a new response-time config (seeded with the standard HSE bands, editable above once created).
          Note: the tier still needs adding to the agency edit form's Tier list separately before any agency can be assigned to it.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Tier name"
            placeholder="e.g. Tier5"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
          />
          <Button variant="contained" onClick={handleCreate} disabled={isLoading}>
            {isLoading ? <CircularProgress size={18} /> : 'Add tier'}
          </Button>
        </Box>
      </Card.Body>
    </Card>
  );
};

// Admin config screen for the HSE "Agency Working" escalation timing:
// how long the principal (Tier1) supplier has to fill a shift, keyed by
// how soon it starts, before each lower tier opens up. Tier1 itself has no
// config here — it always sees a flagged shift immediately, with no
// day-count cap of its own.
const AgencyTierResponseTimesScreen = () => {
  const { data: tierWindows, isLoading, error, refetch } = useGetAgencyTierWindowsQuery();

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Card className="mb-4 custom-card-bg2">
        <Card.Header className="fw-bold">Agency Tier Response Times</Card.Header>
        <Card.Body>
          <Typography variant="h5" gutterBottom>
            Agency Tier Response Times
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Controls how long Tier 1 (the principal supplier) has to fill a shift — based on how soon it
            starts — before it also opens up to a lower tier. The clock starts the moment a shift is
            switched to "Visible" on the Shift List, not from when it was created. Tier 1 always sees a
            visible shift immediately and has no band config here.
          </Typography>
        </Card.Body>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error?.data?.message || 'Failed to load tier response times'}</Alert>
      ) : (
        (tierWindows || []).map((tw) => (
          <TierWindowCard key={tw.tier} tierWindow={tw} onDeleted={refetch} />
        ))
      )}

      <AddTierForm onCreated={refetch} />
    </Box>
  );
};

export default AgencyTierResponseTimesScreen;
